/**
 * WebSocket message types
 * Client ↔ Server communication protocol
 *
 * Based on WEBSOCKET_PROTOCOL.md specification.
 */

// Import SessionEndReason from canonical location for use in this file
import type { SessionEndReason } from './game-session';
import type { Intent } from './intent';
import type { ActionSlot } from '../elasticsearch/combat-mechanics';
import type { CreatureSize } from './combat-mechanics';
import type {
  CampaignJournal,
  EncounterType,
  JournalEvent,
  SceneItem,
  SceneInteractable,
} from './session-state';
import type {
  CharacterPermanent,
  CharacterSession,
  CharacterType,
  ConditionEntry,
  LastSavingThrow,
} from './character';
import type { GridPosition, PresentationRound } from './presentation';
import type { TacticalMap } from './tactical-map';

// Re-export for backward compatibility (consumers importing from websocket.ts)
export type { SessionEndReason };
export type { EncounterType };
export type { CharacterType };

/** Time-of-day period for icon/color selection */
export type TimeOfDayPeriod = 'morning' | 'afternoon' | 'evening' | 'night';

/** Structured in-game time data sent in WebSocket messages */
export interface InGameTimeData {
  /** Human-readable formatted time, e.g. "Day 3, 14:30" */
  formatted: string;
  /** Day number since campaign start (0-indexed internally) */
  day: number;
  /** Hour (0-23) */
  hour: number;
  /** Minute (0-59) */
  minute: number;
  /** Time of day period */
  timeOfDay: TimeOfDayPeriod;
}

/**
 * Wire-format condition entry for WebSocket messages.
 * Can be a simple string or a ConditionEntry object with duration metadata.
 */
export type WireConditionEntry = string | ConditionEntry;

// ============================================================
// Client → Server messages
// ============================================================

/**
 * Messages that clients send to the game server.
 * Matches Rust protocol in backend/game-server/src/session/protocol.rs
 *
 * Two action paths:
 * - `PlayerIntentMessage`: FE-built typed `Intent` from a card / button /
 *   slash menu. Backend skips the LLM agent and runs through `enrich_intent`.
 * - `PlayerTextMessage`: user-typed prose. Backend routes to the LLM intent
 *   agent.
 */
export type ClientMessage =
  | PlayerIntentMessage
  | PlayerTextMessage
  | PlayerMoveMessage
  | PositionIntentMessage
  | ChatMessage
  | PingMessage
  | CombatHighlightAckMessage
  | EndSessionMessage;

/**
 * 3D-board movement rider (combat): the clicked destination as an explicit
 * waypoint path. Queues a movement-only turn input server-side; the
 * collector folds it with the same round's typed/text action into ONE 5E
 * turn. Sent alone = a move-only turn.
 */
export interface PlayerMoveMessage {
  type: 'player_move';
  movement: import('./intent').MovementAction;
  /** See {@link PlayerIntentMessage.scene_id_at_emit}. */
  scene_id_at_emit?: string;
}

/**
 * Pre-combat reposition of the player's OWN PC on the 3D board. Engine
 * validates (passable + unoccupied), persists, and broadcasts
 * `party_positions`. Rejected in combat.
 */
export interface PositionIntentMessage {
  type: 'position_intent';
  x: number;
  y: number;
}

/** One character's board position (see `party_positions`). */
export interface CharacterBoardPosition {
  charId: string;
  x: number;
  y: number;
}

/** Server broadcast after a validated pre-combat reposition. */
export interface PartyPositionsEvent {
  type: 'party_positions';
  positions: CharacterBoardPosition[];
}

/**
 * Turn-level flags carried alongside a typed Intent on `PlayerIntentMessage`.
 *
 * - `reckless_attack` (Barbarian L2+): grants advantage on STR melee attacks
 *   for the turn; replaces the previous `AttackAction.reckless_attack` field
 *   so a standalone card click can ship the declaration without a paired
 *   attack.
 * - `action_surge` (Fighter L2+): take one additional Action this turn
 *   (except Magic — SRD 5.2.1). No action cost. A 2nd-Attack click after a
 *   surge card click routes server-side to `action_surge_action`.
 */
export interface TurnFlags {
  reckless_attack?: boolean;
  action_surge?: boolean;
}

/**
 * FE-built typed Intent — card click, button, or slash + completion menu.
 * Skips the LLM agent and runs through `enrich_intent` directly.
 *
 * `action_slot` defaults to `'action'` server-side; legacy clients shipping
 * only `{type, intent}` keep working. New code MUST set the slot when the
 * source card / shortcut is a bonus-action / reaction / free / movement
 * activation, otherwise the engine consumes the wrong economy.
 */
export interface PlayerIntentMessage {
  type: 'player_intent';
  intent: Intent;
  action_slot?: ActionSlot;
  turn_flags?: TurnFlags;
  /**
   * Scene the FE believed was current when the player clicked. Backend stamps
   * the queued intent with this so the stale-intent guard can drop it if the
   * scene transitions between click and drain. Optional for backward compat;
   * a missing value falls back to the actor's scene at handler time. See
   * scenario 2026-06-01 BUG-9/10 for the rollback-during-flight failure mode.
   */
  scene_id_at_emit?: string;
}

/**
 * User-typed prose. Routes through the LLM intent agent before dispatch.
 */
export interface PlayerTextMessage {
  type: 'player_text';
  text: string;
  /** See {@link PlayerIntentMessage.scene_id_at_emit}. */
  scene_id_at_emit?: string;
}

/**
 * Chat message (in-character speech, broadcast to party).
 */
export interface ChatMessage {
  type: 'chat';
  message: string;
}

/**
 * Heartbeat ping for connection keep-alive.
 */
export interface PingMessage {
  type: 'ping';
}

/** Receipt for a replayable, privately billed combat highlight. */
export interface CombatHighlightAckMessage {
  type: 'combat_highlight_ack';
  id: string;
  displayed: boolean;
}

/**
 * Player-initiated session end (e.g. FE inactivity timeout). Triggers the
 * canonical `handle_session_end` shutdown path on the game-server: scene
 * snapshot, Firestore cleanup, recap generation, lobby notify. Without this
 * the WS just closes — the server keeps generating rounds and burning tokens
 * until its own idle gate fires.
 */
export interface EndSessionMessage {
  type: 'end_session';
  reason?: string;
}

// ============================================================
// Server → Client messages
// ============================================================

export type ServerMessage =
  | RoundResultMessage
  | CombatHighlightReadyMessage
  | MissedRoundsMessage
  | ActionQueuedMessage
  | GameStateMessage
  | ErrorMessage
  | ChatBroadcastMessage
  | PongMessage
  | PlayerReadyMessage
  | AllPlayersReadyMessage
  | PlayerDisconnectedMessage
  | PlayerReconnectedMessage
  | PlayerAwayMessage
  | SceneTransitionMessage
  | SceneImageReadyMessage
  | PartyImageGeneratedMessage
  | CharacterImageUpdatedMessage
  | SessionEndingMessage
  | SessionEndedMessage
  | NarrativeStreamMessage
  | NarrativePreviewMessage
  | RoundFailedMessage
  | RoundStartedMessage
  | IntentRejectedMessage
  | WarmupHoldingMessage
  | SystemNotificationMessage
  // Campaign movement messages (defined in shared/types/websocket.rs but not yet sent by game-server)
  | MapUpdateMessage
  | MovementProposedMessage
  | MovementVoteCastMessage
  | MovementStartedMessage
  | MovementCompleteMessage
  | MovementBlockedMessage
  // Audio — server tells client what to play; client AudioManager
  // handles streaming, fades, and loop crossfade.
  | SoundtrackPlayMessage
  | SoundtrackStopMessage
  | FxPlayMessage
  // 3D board: pre-combat reposition broadcast.
  | PartyPositionsEvent;

/** Private asynchronous first-person combat clip, available for direct streaming. */
export interface CombatHighlightReadyMessage {
  type: 'combat_highlight_ready';
  id: string;
  sender: 'polymyth';
  round: number;
  /** Scene where the attack occurred; stale clips are dropped after travel. */
  scene_id: string;
  char_id: string;
  video_url: string;
  duration_seconds: number;
  direction: 'player_attacks' | 'player_is_hit';
  action_description: string;
  cost_pm: number;
}

// ============================================================
// Game Server Protocol Types (matches Rust protocol.rs)
// ============================================================

/**
 * State change type wire format (matches Rust ChangeTypeWire)
 */
export type ChangeTypeWire =
  | 'hp_change'
  | 'hp_set'
  | 'temp_hp'
  | 'condition_added'
  | 'condition_removed'
  | 'position_change'
  | 'spell_slot_used'
  | 'feature_used'
  | 'concentration_started'
  | 'concentration_ended'
  | 'death_save'
  | 'cover_change'
  | 'gold_change'
  | 'item_acquired'
  | 'item_removed'
  | 'quest_flag'
  | 'quest_objective'
  | 'reputation_change'
  | 'discovery'
  | 'scene_change'
  | 'exhaustion_change'
  | 'xp_gain'
  | 'class_resource_used'
  | 'item_charge_used'
  | 'sorcery_points_delta'
  // Party-panel combat badges. All carry ABSOLUTE values, not deltas, so a
  // client that missed a round can't drift.
  | 'heroic_inspiration'
  | 'death_save_tally'
  | 'last_saving_throw'
  | 'crit_markers'
  // Combat ended: drop every combat-scoped badge on this character.
  | 'combat_badges_cleared'
  | 'grid_position';

/**
 * State change message for round results (matches Rust StateChangeMessage)
 * Note: Uses snake_case to match backend Rust serialization
 */
export interface StateChangeMessage {
  /** Character ID this change applies to */
  char_id: string;
  /** Type of state change */
  change_type: ChangeTypeWire;
  /** Change value (varies by type) */
  value: unknown;
}

/**
 * Character action message for round results (matches Rust CharacterActionMessage)
 * Used to display action bubbles from each character in the chat UI.
 * Note: Uses snake_case to match backend Rust serialization
 */
export interface CharacterActionMessage {
  /** Stable unique ID for dedup (same in preview + round_result) */
  id: string;
  /** Character ID */
  char_id: string;
  /** Character name (for display) */
  char_name: string;
  /** Action type (attack, cast_spell, dodge, move, speak, etc.) */
  action_type: string;
  /** Human-readable description of the action */
  description: string;
  /** Target character ID (if any) */
  target?: string;
  /** Whether the action succeeded */
  success?: boolean;
  /** Damage dealt (if any) */
  damage?: number;
  /** Healing done (if any) */
  healing?: number;
  /** Speaker avatar URL — propagated so the FE renders the right portrait
   * on the first bubble (sceneCharacters update races the preview event). */
  avatar_url?: string;
  /** Speaker avatar emoji fallback */
  avatar_emoji?: string;
  /** Speaker portrait URL (clickable full-size variant) */
  portrait_url?: string;
}

/**
 * Session-level changes in a round (matches Rust SessionChanges)
 * Note: Uses camelCase to match Rust #[serde(rename_all = "camelCase")]
 */
export interface RoundSessionChanges {
  /** Current encounter type (exploration, social, combat) */
  encounterType?: string;
  /** Combat just started (trigger description) */
  combatTrigger?: string;
  /** Combat just ended with this result */
  combatResolution?: string;
  /** Rest completed this round ("short" or "long") */
  restCompleted?: string;
}

// ============================================================
// Round Complete Types (matches Rust websocket.rs RoundCompleteData)
// ============================================================

/**
 * Condition changes for a character
 * Matches Rust ConditionChanges
 */
export interface ConditionChanges {
  /** Conditions to add */
  add?: WireConditionEntry[];
  /** Condition names to remove */
  remove?: string[];
}

/**
 * Position update for a character
 * Matches Rust PositionUpdate
 */
export interface PositionUpdate {
  x?: number;
  y?: number;
  location?: string;
}

/**
 * Character state change (matches Rust CharacterStateChange)
 * Used as intermediate type when processing round_result StateChangeMessage arrays.
 * Note: Uses camelCase to match Rust #[serde(rename_all = "camelCase")]
 */
export interface CharacterStateChange {
  hp?: number;
  hpChange?: number;
  maxHp?: number;
  tempHp?: number;
  conditions?: ConditionChanges;
  position?: PositionUpdate;
  /** Spell slots used: level -> count consumed this round */
  spellSlotsUsed?: Record<number, number>;
  /** Concentration spell name (set when started, null when ended) */
  concentration?: string | null;
  /** Death save result: { success: boolean, critical: boolean } */
  deathSave?: { success: boolean; critical: boolean };
  /** Exhaustion level (0-6) */
  exhaustionLevel?: number;
  /** Cover type change */
  cover?: 'none' | 'half' | 'three_quarters' | 'total';
  /** Items acquired this round */
  itemsAcquired?: Array<{
    item_name: string;
    item_id: string;
    quantity?: number;
    magical?: boolean;
  }>;
  /** Items removed this round */
  itemsRemoved?: string[];
  /** Gold change (positive = gained, negative = lost) */
  goldChange?: number;
  /** Features used this round */
  featuresUsed?: string[];
  /** XP gained this round */
  xpGained?: number;
  /**
   * Class resources consumed this round, accumulated by name.
   * Mirrors `CharacterStateChanges.class_resources_used` server-side. The FE
   * applies these via `applyClassResourceDelta` which mirrors the Rust
   * resource-name → session field mapping in `apply_state_changes`.
   */
  classResourcesUsed?: Record<string, number>;
  /**
   * Magic-item charges consumed this round, keyed by item_id (opaque).
   * The FE finds the item in `equipment` by id and decrements its `charges`.
   */
  itemChargesUsed?: Record<string, number>;
  /**
   * Sorcery Points delta (positive = gain via Slot→SP, negative = spend).
   * Sorcery points use "remaining" semantics in the session
   * (`class_resources.sorcery_points_remaining`); the delta is added directly
   * (clamped at 0).
   */
  sorceryPointsDelta?: number;

  // === Party-panel combat badges (absolute values) ===
  /** Holds Heroic Inspiration after this round. */
  heroicInspiration?: boolean;
  /** Death save tally after this round. */
  deathSaveTally?: { successes: number; failures: number };
  /** Most recent saving throw resolved this round. */
  lastSavingThrow?: LastSavingThrow;
  /**
   * Per-round crit markers. Both flags always arrive together so last round's
   * marker is cleared even when only one of them changed.
   */
  critMarkers?: { dealt: boolean; taken: boolean };
  /**
   * Combat ended: clear every combat-scoped badge (last saving throw, cover,
   * both crit markers). An explicit signal rather than four cleared values,
   * because `lastSavingThrow` already spends its absent state on "no change".
   */
  combatBadgesCleared?: boolean;
  /**
   * The recipient's own grid square, for the tactical map. Sent only to the
   * character it belongs to: observable views carry everyone else's position but
   * deliberately exclude the recipient, so this is the only channel that moves
   * the player's own token during a fight.
   */
  gridPosition?: GridPosition;
}

/**
 * Session-level changes (matches Rust SessionChanges)
 * Note: Uses camelCase to match Rust #[serde(rename_all = "camelCase")]
 */
export interface SessionChanges {
  /** Combat just ended with this result */
  combatResolution?: string;
  /** Current encounter type (exploration, social, combat) */
  encounterType?: EncounterType;
  /** Combat just started (trigger description) */
  combatTrigger?: string;
  /** Rest completed this round ("short" or "long") */
  restCompleted?: string;
}

/**
 * Scene item added (matches Rust SceneItemAdded)
 */
export interface SceneItemAdded {
  id: string;
  name: string;
  type?: string;
  location: string;
  lootable?: boolean;
  quantity?: number;
}

/**
 * Scene-level changes (matches Rust SceneChanges)
 * Note: Uses camelCase to match Rust #[serde(rename_all = "camelCase")]
 */
export interface SceneChanges {
  itemsAdded?: SceneItemAdded[];
  itemsRemoved?: string[];
  timeIncrementSeconds?: number;
}

/**
 * Private message (whisper) to a specific player
 * Note: Uses snake_case to match Rust ServerMessage::RoundResult serialization
 */
export interface PrivateMessage {
  from_char_id: string;
  from_char_name: string;
  message: string;
  /** Speaker avatar URL — same race-fix as CharacterActionMessage. */
  avatar_url?: string;
  /** Speaker avatar emoji fallback */
  avatar_emoji?: string;
  /** Speaker portrait URL (clickable full-size variant) */
  portrait_url?: string;
}

/**
 * Rejected intent — structured wire format. The FE composes English from
 * `intentKind` + names; `devReason` is browser-console / log fodder only,
 * NEVER rendered. See `composeRejectionMessage` in `gameStore.ts` and the
 * matching `RejectedIntent` Rust struct in
 * `backend/shared/src/char_state/component.rs`.
 */
export interface RejectedIntent {
  /**
   * Raw input text — diagnostic only, never rendered. Required at the wire
   * level (validated by zod in game-connection-manager.ts); typed optional
   * here so consumers that don't care about it don't need to thread it
   * through every callsite (the renderer only uses structured fields).
   */
  originalIntent?: string;
  /** Intent variant tag, e.g. "Attack", "CastSpell". Required for English. */
  intentKind?: string;
  /** Item / spell / ability / destination display name (resolved). */
  objectName?: string;
  /** Target display name (resolved). */
  subjectName?: string;
  /** Optional short English suffix ("out of range", etc.). */
  playerReason?: string;
  /** Technical reason — server logs + FE console.warn only, never UI. */
  devReason: string;
}

// ============================================================
// Observable Character Views (matches Rust ObservableCharacterView)
// ============================================================

/**
 * Health state as perceived by an observer (matches Rust HealthState)
 * Instead of exact HP, observers see rough health categories
 */
export type HealthState = 'uninjured' | 'wounded' | 'bloodied' | 'critical' | 'dying' | 'dead';

/**
 * NPC disposition towards the party (matches Rust Disposition)
 */
export type Disposition = 'friendly' | 'neutral' | 'hostile';

/**
 * Observable character view — what any character can perceive about another.
 * Matches Rust ObservableCharacterView (camelCase serialization).
 *
 * No exact HP, spell slots, or internal mental states — only what's
 * physically observable (health state, visible conditions, equipment).
 */
export interface ObservableCharacterView {
  /** Character ID */
  id: string;
  /** Character name */
  name: string;
  /** Character type (pc, npc, monster, animal) */
  charType: CharacterType;
  /** Portrait image URL */
  portraitUrl?: string;
  /** Avatar image URL */
  avatarUrl?: string;
  /** Avatar emoji fallback */
  avatarEmoji?: string;
  /** Player-facing 3D look block (structured, camelCase; see the
   * character-appearance PRD). Mirrors the connect snapshot's lift so
   * mid-session spawns render their authored look. */
  appearance?: Record<string, unknown>;
  /** Species (optional) */
  species?: string;
  /** SRD creature size — what the tactical map derives its footprint from */
  size?: CreatureSize;
  /** Character level */
  level?: number;
  /** Character class (e.g., "Fighter", "Ranger") */
  class?: string;
  /** Rough health state (not exact HP) */
  healthState: HealthState;
  /** Physically visible conditions only (Prone, Restrained, etc.) */
  visibleConditions: string[];
  /**
   * Exhaustion level, and the penalty from a recent resurrection. Both ride the
   * observable view so a teammate's party-panel badges stay live: this view is
   * rebuilt every round_result, while only the signed-in player's own character
   * gets a full mid-session re-sync. Absent when zero, and always absent for
   * monster observers (the server clears them, since the AI prompts serialize
   * this whole object).
   */
  exhaustionLevel?: number;
  resurrectionPenalty?: number;
  /** Currently equipped weapon */
  equippedWeapon?: string;
  /** Currently equipped armor */
  equippedArmor?: string;
  /** Currently equipped shield */
  equippedShield?: string;
  /** Wondrous items visibly worn (amulets, cloaks, rings, etc.) */
  equippedWondrousItems?: string[];
  /** Whether concentrating on a spell (no specifics) */
  isConcentrating: boolean;
  /**
   * Spell name when the observer is permitted to see it (own char + party
   * members). Hostile / neutral observers see undefined even when
   * isConcentrating is true. Server-side friendly gate; FE renders the pill
   * with spell name only when this field is populated.
   */
  concentrationSpell?: string;
  /** Whether prone */
  isProne: boolean;
  /** Combat position */
  position?: { x?: number; y?: number; zone?: string };
  /** NPC disposition towards party */
  disposition?: Disposition;
  /**
   * The NPC's exact stored attitude.
   *
   * `disposition` above is the three-value read the engine reasons over, and the
   * server derives it lossily: `unfriendly` folds onto `neutral`, `allied` onto
   * `friendly`. The pickers gate on `unfriendly` specifically, so they need the
   * unfolded value.
   *
   * The server holds four attitudes this protocol does not model (`frightened`,
   * `suspicious`, `indifferent`, `unknown`). Those arrive AS `neutral` rather
   * than being omitted, matching where the disposition fold puts them. The
   * field is absent only when the server holds no attitude at all, which is
   * where falling back to `disposition` applies.
   */
  attitude?: 'hostile' | 'unfriendly' | 'neutral' | 'friendly' | 'allied';
  /** Whether this character is a party member */
  isPartyMember: boolean;
  /** Whether this character is the party leader */
  isPartyLeader?: boolean;
  /** Whether this character is an ally (relative to observer) */
  isAlly?: boolean;
  /** Distance from observer in feet */
  distanceFt?: number;
}

/**
 * Travel destination for /travel command (matches Rust TravelDestinationWire)
 * Scene exits are nearby (same location), paths are distant (different location).
 * Only includes destinations that pass quest flag / access condition checks.
 */
export interface TravelDestination {
  id: string;
  name: string;
  destinationType: 'scene' | 'path';
  travelMinutes?: number;
  direction?: string;
  description?: string;
}

/**
 * A named party-wide buff granted by a currently-true quest flag (matches Rust
 * ActiveBoonWire). Effect lines are formatted server-side so web and mobile
 * show identical wording. Always a full list, never a delta: a boon whose flag
 * flipped back off simply stops appearing.
 */
export interface ActiveBoon {
  /** Boon name, e.g. "Infernal Vigor" */
  name: string;
  /** One display line per effect, e.g. "+1d4 fire damage" */
  effects: string[];
}

/**
 * Round result from game server - current wire format
 * Note: Uses snake_case to match Rust ServerMessage::RoundResult serialization
 *
 * Per-player message: Each player receives their own version with private data filtered.
 */
/**
 * One character present in the scene or the party this round. Mirrors
 * `RoundRosterEntryWire` in `backend/game-server/src/session/protocol.rs`.
 *
 * Carries no name or state on purpose: this answers "who is here", not "what
 * can I see". Clients already hold the descriptive data from `game_state`.
 */
export interface RoundRosterEntry {
  charId: string;
  isPartyMember: boolean;
}

export interface RoundResultMessage {
  type: 'round_result';
  /** Round number */
  round: number;
  /** Scene id this round was executed on. Used by the connection manager to drop stale-scene messages during scene transitions. */
  scene_id: string;
  /** Main narrative for all players */
  narrative: string;
  /** State changes for characters (typed changes) */
  changes: StateChangeMessage[];
  /** Whether combat is active */
  in_combat: boolean;
  /** Per-character actions with rich data */
  character_actions?: CharacterActionMessage[];
  /** Session-level changes (encounter type, combat triggers) */
  session_changes?: RoundSessionChanges;
  /** Pipeline processing time in milliseconds */
  processing_time_ms?: number;
  /** Current in-game time (updated each round) */
  in_game_time?: InGameTimeData;
  /** Party leader's character ID, re-sent each round; `null` when there is no leader */
  leader_character_id?: string | null;

  // ════════════════════════════════════════════════════════════════
  // ROSTER (who is here — NOT perception-filtered)
  // ════════════════════════════════════════════════════════════════

  /**
   * Every character in the scene or the party this round, including the
   * recipient, regardless of whether the recipient can perceive them.
   *
   * This is the authoritative answer to "who exists". The observable lists
   * below are a PERCEPTION overlay and must never be used to decide presence
   * or party membership: a monster that turns Invisible drops out of them
   * while staying a legal attack target, so trimming a roster on them deletes
   * an enemy mid-fight.
   *
   * Absent means a payload that predates this field — treat that as
   * "don't trim", never as "nobody is here".
   */
  roster?: RoundRosterEntry[];

  // ════════════════════════════════════════════════════════════════
  // OBSERVABLE CHARACTER STATE (computed from player's perspective)
  // ════════════════════════════════════════════════════════════════

  /** Party members as observable views (PCs, companions) */
  observable_party_members?: ObservableCharacterView[];
  /** Non-party characters as observable views (NPCs, monsters) */
  observable_non_party_members?: ObservableCharacterView[];

  // ════════════════════════════════════════════════════════════════
  // PER-PLAYER PRIVATE DATA (filtered to this player only)
  // ════════════════════════════════════════════════════════════════

  /** Private narrative for THIS player's character (whisper contents, personal perceptions) */
  my_char_narrative?: string;
  /** Private messages (whispers) TO this player's character */
  my_private_messages?: PrivateMessage[];
  /** Rejected intents for THIS player (enricher failures) */
  my_rejected_intents?: RejectedIntent[];

  // ════════════════════════════════════════════════════════════════
  // FULL CHARACTER DATA (authoritative snapshot for this player)
  // ════════════════════════════════════════════════════════════════

  /** Full permanent data for this player's character (AC, abilities, equipment, spells, etc.) */
  my_character_permanent?: CharacterPermanent;
  /** Full session data for this player's character (HP, spell slots used, conditions, etc.) */
  my_character_session?: CharacterSession;

  // ════════════════════════════════════════════════════════════════
  // SCENE ITEMS & INTERACTABLES (shared with all players)
  // ════════════════════════════════════════════════════════════════

  /** Scene items (loot, quest items) — snake_case matches RoundResult variant */
  scene_items?: SceneItem[];
  /** Scene interactable objects (doors, chests, levers) */
  scene_interactables?: SceneInteractable[];
  /** New journal events generated this round */
  new_journal_events?: JournalEvent[];
  /** Reachable travel destinations (updated each round) */
  travel_destinations?: TravelDestination[];
  /** Party-wide boons currently granted by quest flags (full list each round) */
  active_boons?: ActiveBoon[];
  /**
   * Renderer-facing playback script for this round (3D combat world).
   * Per-recipient: events and entity snapshots are filtered to the same
   * visible-entity set as character_actions. Absent when mechanics did not
   * execute this round (pending party-decision rounds).
   */
  render_events?: PresentationRound | null;
}

/**
 * Round summary for missed rounds (sent on reconnection)
 */
export interface RoundSummary {
  round: number;
  /** Main narrative for all players */
  narrative: string;
  /** ISO 8601 timestamp when round completed */
  timestamp: string;
  /** Scene the round played in (absent on summaries from older servers) */
  scene_id?: string;
}

/**
 * Sent on reconnection with rounds the player missed while disconnected
 */
export interface MissedRoundsMessage {
  type: 'missed_rounds';
  /** Rounds missed while disconnected, in chronological order */
  rounds: RoundSummary[];
  /** Round when player disconnected (snake_case from backend inline enum variant) */
  disconnected_at_round: number;
  /** Current round (snake_case from backend inline enum variant) */
  current_round: number;
}

/**
 * Acknowledges player's action has been queued
 */
export interface ActionQueuedMessage {
  type: 'action_queued';
  characterId: string;
  /** Brief description of the action */
  action: string;
}

// GameSessionStatus is imported from game-session.ts
// Re-export for convenience
import type { GameSessionStatus } from './game-session';
export type { GameSessionStatus } from './game-session';

/**
 * Equipment item for game character (matches unified Item type)
 */
export interface GameEquipmentItem {
  item_id?: string;
  name: string;
  item_kind?: string;
  /** @deprecated Use item_kind. Kept for backward compat with gameStore delta logic. */
  type?: string;
  attack_bonus?: number;
  damage?: string;
  ac_bonus?: number;
  properties?: string[];
  quantity?: number;
  description?: string;
  /**
   * Remaining charges for charge-based magic items (Wand of Magic Missiles, etc).
   * Decremented in place by `item_charge_used` wire events; the engine emits a
   * separate `item_removed` when the item is fully consumed.
   */
  charges?: number;
  /**
   * Quest-critical item flag (default false; omitted from wire when false).
   * Tagged in module data on items whose loss would break the quest
   * (Amulet of Theron in Goldvale, etc.). The slash `/give` picker filters
   * these out so the player can't accidentally hand the artifact to a party
   * member who later wanders off. `/drop`, `/equip`, `/use` remain available.
   * Mirrors the Rust `Item::is_quest_item` field (2026-05-31).
   */
  is_quest_item?: boolean;
}

/** Item kinds that are equippable (vs. consumables, tools, gear). Drives the
 *  `/equip` slash picker. Shared so the FE doesn't enumerate kinds in two places.
 *
 *  `clothing` is intentionally NOT here: every clothing item in the data
 *  today is mundane (Scholar's Robes, Traveler's Clothes, Vestments,
 *  …) and confers no mechanical effect, so equipping it would be a
 *  no-op slot occupant at best. Magic robes / cloaks / hats are
 *  classified as `wondrous_item` and remain equippable through the
 *  picker. */
export const EQUIPPABLE_ITEM_KINDS = ['weapon', 'armor', 'wondrous_item'] as const;

/**
 * Class feature or trait
 */
export interface GameFeature {
  name: string;
  description?: string;
  max_uses?: number;
  recharge?: string;
}

/**
 * Feat info
 */
export interface GameFeat {
  name: string;
  description?: string;
  source?: string;
}

/**
 * Spellcasting info for game character
 */
export interface GameSpellcastingInfo {
  ability?: string;
  spell_save_dc?: number;
  spell_attack_bonus?: number;
  ritual_casting?: boolean;
  max_spell_slots?: Record<string, number>;
  spells_known_by_level?: Record<string, (string | { name: string })[]>;
  always_prepared?: (string | { name: string; base_level?: number })[];
}

// `GridPosition` (grid coordinate on the tactical map, matching Rust
// `combat_mechanics::Position`: a square grid, 8 neighbours, diagonal counts
// as 5ft per SRD 5.2) is declared once in `./presentation` and imported above.
// Both modules used to declare it, which made the `@danda/shared` barrel
// ambiguous (TS2308). `presentation` owns it because it imports nothing, so
// the dependency stays one-way.

/**
 * Character info for game state
 */
export interface GameCharacterInfo {
  charId: string;
  name: string;
  hp: number;
  maxHp: number;
  /** Active conditions (backend sends WireConditionEntry: string or object with name + duration metadata) */
  conditions: WireConditionEntry[];
  /** Initiative roll (in combat) */
  initiative?: number;
  /** Avatar URL for character portrait */
  avatarUrl?: string;
  /** Portrait URL (full portrait image) */
  portraitUrl?: string;
  /** Armor Class */
  ac?: number;
  /** Character level */
  level?: number;
  /** Character class (e.g., "Fighter", "Wizard") */
  class?: string;
  /** Character species/race (e.g., "Human", "Elf") */
  species?: string;
  /**
   * SRD creature size, for the tactical map footprint. Only the observable-view
   * path fills this (the connect snapshot ships the same value inside
   * `permanent.size`), so readers should fall back to `permanent?.size`.
   */
  size?: CreatureSize;
  /** Character type: pc, npc, monster, animal */
  type?: CharacterType;
  /** Whether this character is a party member (PCs and party NPCs) */
  isPartyMember?: boolean;
  /** NPC attitude toward party (hostile, unfriendly, neutral, friendly, allied) */
  attitude?: 'hostile' | 'unfriendly' | 'neutral' | 'friendly' | 'allied';
  /** Backend-authoritative death flag (CharacterData::is_dead()). The FE trusts
   *  this for DEAD vs UNCONSCIOUS instead of re-deriving from HP + conditions. */
  isDead?: boolean;
  /** True while the owning player is disconnected past grace ("stepped away") */
  away?: boolean;
  /** Ability scores */
  abilities?: {
    str?: number;
    dex?: number;
    con?: number;
    int?: number;
    wis?: number;
    cha?: number;
  };
  /** Proficiency bonus */
  proficiencyBonus?: number;
  /** Speed in feet */
  speed?: number;
  /** Temporary HP */
  tempHp?: number;
  /** Death save successes (0-3) */
  deathSaveSuccesses?: number;
  /** Death save failures (0-3) */
  deathSaveFailures?: number;
  /** Background (e.g., "Sage", "Soldier") */
  background?: string;
  /** Avatar emoji */
  avatarEmoji?: string;
  /** Player-facing 3D look block (structured, camelCase). The world
   * renderer maps it to a hero model via heroAppearanceFor. */
  appearance?: Record<string, unknown>;
  /** Equipment items */
  equipment?: GameEquipmentItem[];
  /** @deprecated Use session.equipped_slots. Currently equipped item_ids. */
  equippedItems?: string[];
  /** Class features */
  classFeatures?: GameFeature[];
  /** Species/racial traits */
  speciesTraits?: GameFeature[];
  /** Feats */
  feats?: GameFeat[];
  /** Spellcasting info */
  spellcasting?: GameSpellcastingInfo;
  /** Prepared spells with base level (0 = cantrip, 1-9 = leveled spell).
   *  Mirrors Rust SpellData.base_level which uses 0 for cantrips per SRD 5.2. */
  preparedSpells?: { name: string; level: number }[];
  /** Spell slots used by level */
  spellSlotsUsed?: Record<number, number>;
  /** Experience points */
  xp?: number;
  /** Hit die (e.g., "d8", "d10") */
  hitDie?: string;
  /** Senses */
  senses?: {
    passive_perception?: number;
    darkvision?: number;
    blindsight?: number;
    tremorsense?: number;
    truesight?: number;
  };
  /** Whether the character is concentrating on a spell. Set for ALL observers. */
  isConcentrating?: boolean;
  /**
   * The spell name being concentrated on. Server-side friendly-gated —
   * `null` when the viewer isn't permitted to see the identity (hostile /
   * neutral observer). `undefined` when the character isn't concentrating
   * at all.
   */
  concentrationSpell?: string | null;
  /** Observable health state (for other characters — not exact HP) */
  healthState?: HealthState;

  // P0.3: Class resources (Focus Points, rage uses, etc.)
  /** Class resource pools */
  classResources?: Record<string, unknown>;

  // P0.4: Legendary creature resources
  /** Remaining legendary actions */
  legendaryActionsRemaining?: number;
  /** Remaining legendary resistances */
  legendaryResistancesRemaining?: number;
  /** Recharge abilities status */
  rechargeAbilities?: Record<string, boolean>;

  // P1.1: Exhaustion
  /** Exhaustion level (0-6) */
  exhaustionLevel?: number;

  /** Active resurrection penalty: -N to all d20 tests (SRD 2024), reduced by 1 per long rest */
  resurrectionPenalty?: number;

  // P1.3: Grapple tracking
  /** Characters this character is grappling */
  grappling?: string[];
  /** Character grappling this character */
  grappledBy?: string | null;

  // P2.1: Heroic inspiration
  /** Whether character has Heroic Inspiration */
  heroicInspiration?: boolean;

  // P2.2: Feature uses
  /** Map of feature name to remaining uses */
  featureUses?: Record<string, number>;

  // P2.3: Cover state
  /** Current cover status */
  cover?: 'none' | 'half' | 'three_quarters' | 'total';

  /**
   * Most recent saving throw this character rolled. Cleared on combat exit.
   * Inner keys are snake_case (the Rust `LastSavingThrow` has no `rename_all`).
   */
  lastSavingThrow?: LastSavingThrow;
  /** Landed a critical hit this round. Cleared at round start. */
  critDealtThisRound?: boolean;
  /** Took a critical hit this round. Cleared at round start. */
  critTakenThisRound?: boolean;
  /**
   * Grid position, for the tactical map. Observable views deliberately exclude
   * the recipient, so this is what puts the player's own token on the board.
   */
  position?: GridPosition;

  /** Full permanent character data (sent on initial connect for CharDetailsPanel) */
  permanent?: CharacterPermanent;
  /** Full session character data (sent on initial connect for CharDetailsPanel) */
  session?: CharacterSession;
}

/**
 * Scene info for game state
 */
export interface GameSceneInfo {
  /** Stable scene id — paired with `name` for FE filtering of stale-scene messages during transitions. */
  sceneId: string;
  name: string;
  description?: string;
  imageUrl?: string;
  videoUrl?: string;
  /** Encounter image URL (combat variant, fallback if no video) */
  encounterImageUrl?: string;
  /** Encounter video URL (combat variant, preferred over encounterImageUrl) */
  encounterVideoUrl?: string;
  /** Layered tactical map for the 3D combat board (present when the scene ships one). */
  tacticalMap?: TacticalMap | null;
}

/**
 * Initiative entry for combat turn order (wire format)
 * Matches Rust InitiativeEntryWire (uses camelCase for JSON serialization)
 */
export interface InitiativeEntryWire {
  /** Character ID */
  charId: string;
  /** Character name (for display) */
  charName: string;
  /** Initiative roll result */
  initiative: number;
  /** Whether this character has the current turn */
  isCurrent: boolean;
}

/**
 * Initial state sent on connection or when requested
 * Matches WEBSOCKET_PROTOCOL.md specification
 */
export interface GameStateMessage {
  type: 'game_state';
  state: {
    sessionId: string;
    currentRound: number;
    /** Session status */
    status: GameSessionStatus;
    /** Current encounter type: exploration (default), social, or combat. Combat is active when encounterType === 'combat'. */
    encounterType?: EncounterType;
    characters: GameCharacterInfo[];
    /** Scene information */
    scene: GameSceneInfo;
    /** Structured in-game time data */
    inGameTime: InGameTimeData;
    /** Current player's character ID (for identifying player's own character) */
    playerCharacterId?: string;
    /** Party leader's character ID; `null` when the party has no leader */
    leaderCharacterId?: string | null;
    /** Current turn character ID (for combat) */
    currentTurn?: string;
    /** Module title for display */
    moduleTitle?: string;
    /** Module hero image URL for background */
    moduleHeroImage?: string;
    /** Party name for display */
    partyName?: string;
    /** Full initiative order (combat only) */
    initiativeOrder?: InitiativeEntryWire[];
    /** Party image URL (updated on membership change) */
    partyImageUrl?: string;
    /** World/campaign map image URL */
    mapUrl?: string;
    /** World/campaign map video URL */
    mapVideoUrl?: string;
    /** Scene items (loot, quest items) */
    sceneItems?: SceneItem[];
    /** Scene interactable objects (doors, chests, levers) */
    sceneInteractables?: SceneInteractable[];
    /** LLM-generated recap from the previous session (from campaign journal) */
    lastSessionRecap?: string;
    /** Campaign journal (player-visible subset) */
    campaignJournal?: CampaignJournal;
    /** Reachable travel destinations for /travel command */
    travelDestinations?: TravelDestination[];
    /** Party-wide boons currently granted by quest flags */
    activeBoons?: ActiveBoon[];
  };
}

/**
 * Error occurred processing request
 */
export interface ErrorMessage {
  type: 'error';
  message: string;
  /** Error code for client handling */
  code?: string;
}

/**
 * Chat message from another player or system.
 * Matches game-server/protocol.rs ChatBroadcast.
 */
export interface ChatBroadcastMessage {
  type: 'chat_broadcast';
  /** Character ID who said it */
  char_id: string;
  /** Character name (for display) */
  char_name: string;
  /** The message content */
  message: string;
}

/**
 * Response to ping (keep-alive)
 */
export interface PongMessage {
  type: 'pong';
}

// ============================================================
// New Game Event Messages
// ============================================================

/**
 * Sent when a player signals ready
 */
export interface PlayerReadyMessage {
  type: 'player_ready';
  userId: string;
  characterId?: string;
  characterName?: string;
  ready: boolean;
  timestamp: string;
}

/**
 * Sent when all players are ready (round will execute)
 */
export interface AllPlayersReadyMessage {
  type: 'all_players_ready';
  playerCount: number;
  timestamp: string;
}

/**
 * Sent when a player disconnects
 */
export interface PlayerDisconnectedMessage {
  type: 'player_disconnected';
  userId: string;
  characterId?: string;
  characterName?: string;
  /** Grace period before character removal (ms) */
  gracePeriodMs: number;
}

/**
 * Sent when a player reconnects
 */
export interface PlayerReconnectedMessage {
  type: 'player_reconnected';
  userId: string;
  characterId?: string;
  characterName?: string;
}

/**
 * Player away-status change: grace period expired (away: true) or the player
 * came back (away: false). The roster flips "stepped away" on this signal.
 */
export interface PlayerAwayMessage {
  type: 'player_away';
  characterId: string;
  characterName?: string;
  away: boolean;
}

/**
 * Sent when scene transitions
 */
export interface SceneTransitionMessage {
  type: 'scene_transition';
  /** Previous scene name */
  fromScene: string;
  /** New scene info */
  toScene: GameSceneInfo;
  /** Updated in-game time (structured data) */
  inGameTime: InGameTimeData;
  timestamp: string;
  /** Scene items at the new scene */
  sceneItems?: SceneItem[];
  /** Scene interactable objects at the new scene */
  sceneInteractables?: SceneInteractable[];
  /**
   * Reachable travel destinations for the NEW scene's `/travel` slash menu.
   * Sent on the same frame as the scene swap so the dropdown reflects the
   * destination scene's exits/paths immediately, instead of going empty
   * until the next RoundResult lands 1–2 rounds later (Bug 3B from the
   * travel pre-round hoist plan).
   */
  travelDestinations?: TravelDestination[];
  /**
   * Party-wide boons after the transition. Scene arrival can flip quest flags,
   * so the pills refresh on the same frame as the scene swap.
   */
  activeBoons?: ActiveBoon[];
}

/**
 * Sent when scene image generation completes (async after scene transition).
 * Matches game-server/protocol.rs SceneImageGenerated.
 */
export interface SceneImageReadyMessage {
  type: 'scene_image_generated';
  /** Scene name this image is for */
  scene_name: string;
  /** Scene image URL (GCS public URL) */
  image_url: string;
  /** Image ID */
  image_id: string;
  /** Generation duration in milliseconds */
  duration_ms: number;
}

/**
 * Sent when party image generation completes (async after membership change).
 * Matches game-server/protocol.rs PartyImageGenerated.
 */
export interface PartyImageGeneratedMessage {
  type: 'party_image_generated';
  /** Party image URL (GCS public URL) */
  image_url: string;
  /** Image ID */
  image_id: string;
  /** Generation duration in milliseconds */
  duration_ms: number;
}

/**
 * Sent when a character's portrait/avatar finishes generating AFTER the player
 * one-click-started into the game (the lobby generates art asynchronously and
 * the quick-start enters the session before it lands). Lets the client patch
 * that character's art live instead of showing the emoji fallback all session.
 * Matches game-server/protocol.rs CharacterImageUpdated. At least one URL set.
 */
export interface CharacterImageUpdatedMessage {
  type: 'character_image_updated';
  /** Character whose art changed (matches GameCharacterInfo.charId) */
  char_id: string;
  /** New portrait URL, if the portrait was (re)generated */
  portrait_url?: string;
  /** New avatar URL, if the avatar was (re)generated */
  avatar_url?: string;
}

/**
 * Sent immediately when session starts ending — before any I/O (save, billing, etc.).
 * Frontend shows "Session ending... saving your progress" banner.
 */
export interface SessionEndingMessage {
  type: 'session_ending';
  /** End reason (debug-level, e.g. "PartyVote", "Tpk") */
  reason: string;
  /**
   * True if this is a training duel — the recap UI (Master Kira coaching
   * placeholder + post-recap inline display) is gated on this. Sourced from
   * the WS event so we don't depend on the `game-session-duel-portraits`
   * localStorage hint, which is for portrait loading and gets cleared on
   * unrelated paths.
   */
  isTrainingDuel: boolean;
}

/**
 * Sent when session ends
 */
export interface SessionEndedMessage {
  type: 'session_ended';
  /** Reason for ending */
  reason: SessionEndReason;
  /** Final round number */
  finalRound: number;
  /** Session summary (optional) */
  summary?: string;
  /** Party ID to return to lobby (for lobby reconnection) */
  partyId?: string;
  /** Module ID that was completed (if reason is module_completed) */
  moduleId?: string;
  /** Absolute in-game time at session end (total seconds since day 0) */
  finalGameTimeSeconds?: number;
  /**
   * True if this is a training duel. See `SessionEndingMessage.isTrainingDuel`
   * for why this is on the event instead of inferred from localStorage.
   */
  isTrainingDuel: boolean;
  timestamp: string;
}

/**
 * Streaming narrative chunk from CharStateAgent.
 * Matches game-server/protocol.rs NarrativeStream.
 *
 * Sent as LLM generates narrative text, enabling real-time display.
 * Frontend should accumulate chunks until `done: true`.
 */
export interface NarrativeStreamMessage {
  type: 'narrative_stream';
  /** Round this narrative belongs to */
  round: number;
  /** Scene id at the time this chunk was emitted. The connection manager drops chunks streaming from the prior scene during a mid-stream SceneTransition race. */
  scene_id: string;
  /** Text chunk (partial narrative) */
  chunk: string;
  /** True when this is the final chunk */
  done: boolean;
}

/**
 * Early narrative preview sent after Stage 2 (RoundAgent) completes,
 * before Stage 3 (StateTransformer). Lets players see narrative + character
 * actions ~10-15s earlier than waiting for the full round_result.
 *
 * Matches game-server/protocol.rs ServerMessage::NarrativePreview.
 */
export interface NarrativePreviewMessage {
  type: 'narrative_preview';
  /** Round number this preview belongs to */
  round: number;
  /** Scene id this preview was generated on. See `RoundResultMessage.scene_id`. */
  scene_id: string;
  /** Main narrative text (from NARRATIVE section) */
  narrative: string;
  /** Character action chat bubbles (mechanics + narrative actions) */
  character_actions?: CharacterActionMessage[];
  /** Private narrative for THIS player's character (from PRIVATE_NARRATIVES) */
  my_char_narrative?: string;
  /** Private messages (whispers targeting this player from Stage 1 communication events) */
  my_private_messages?: PrivateMessage[];
}

// ============================================================
// Round Failed Message (from game-server/protocol.rs RoundFailed)
// ============================================================

/**
 * Round execution failed — sent instead of round_result when the pipeline
 * exhausts all retries. Carries the explicit round number so the frontend
 * can retract any phantom preview messages for that round.
 *
 * Matches game-server/protocol.rs ServerMessage::RoundFailed.
 */
export interface RoundFailedMessage {
  type: 'round_failed';
  /** Round number that failed */
  round: number;
  /** User-facing error message */
  message: string;
}

/**
 * Out-of-round rejection delivery (wait-for-player-intent test mode only).
 * Normally rejections ride `RoundResultMessage.my_rejected_intents`; in wait
 * mode a rejected input queues no intent so no round fires; this standalone
 * message carries the rejection bubble to the player immediately.
 *
 * Matches game-server/protocol.rs ServerMessage::IntentRejected.
 */
export interface IntentRejectedMessage {
  type: 'intent_rejected';
  /** Rejected intents for THIS player (same shape as RoundResult's field) */
  my_rejected_intents: RejectedIntent[];
}

/**
 * Emitted at the start of round execution (top of spawn_round_execution).
 * Fired via an unbuffered direct send so it arrives DURING the warmup window
 * before game_state is drained — lets the frontend turn the thinking indicator
 * on immediately instead of waiting for the post-warmup drain burst.
 *
 * Also sent from add_connection when a late joiner connects mid-warmup, so
 * players joining after the round has started still see the indicator.
 */
export interface RoundStartedMessage {
  type: 'round_started';
  /** Round number that is about to execute */
  round: number;
}

/**
 * Connection-alive signal sent when a client connects during the warmup
 * window. `game_state` is held in the warmup buffer until gate release (can
 * be 10s+), so without this signal the client sees nothing — its WS watchdog
 * fires a reconnect, which races session creation and can loop. This frame
 * tells the client "session accepted, be patient."
 *
 * Matches Rust `ServerMessage::WarmupHolding`.
 */
export interface WarmupHoldingMessage {
  type: 'warmup_holding';
  /** Current session round (for logging / display only) */
  round: number;
}

/**
 * Informational lifecycle message shown to the player in chat.
 *
 * Used for events like "The party leader has reconnected. Session end
 * cancelled.", "Player X has left the game (disconnected for too long)",
 * and "The party leader has disconnected. Session will end in N seconds...".
 *
 * Distinct from `ErrorMessage` — system notifications are NOT technical
 * failures and should be rendered as plain system info in chat, never with
 * the generic "portal glitches" fallback text.
 *
 * Matches Rust `ServerMessage::SystemNotification`.
 */
export interface SystemNotificationMessage {
  type: 'system_notification';
  message: string;
}

// ============================================================
// Campaign Movement Messages (defined in shared/websocket.rs, not yet sent by game-server)
// ============================================================

/**
 * Campaign map state update.
 * Sent when the party's map knowledge changes.
 */
export interface MapUpdateMessage {
  type: 'map_update';
  /** Current location ID */
  currentLocationId: string;
  /** Current scene ID */
  currentSceneId: string;
  /** Path to map image (optional) */
  mapImage?: string;
  /** Discovered location IDs */
  discoveredLocations: string[];
  /** Discovered scene IDs */
  discoveredScenes: string[];
  /** ISO timestamp */
  timestamp: string;
}

/**
 * Movement proposed - triggers party vote UI.
 * Sent when a character proposes moving to a new location/scene.
 */
export interface MovementProposedMessage {
  type: 'movement_proposed';
  /** Proposal ID for voting */
  proposalId: string;
  /** Character ID who proposed */
  proposerId: string;
  /** Target name (human-readable) */
  targetName: string;
  /** Whether this is travel (cross-location) or scene movement */
  isTravel: boolean;
  /** Travel time description (optional) */
  travelTime?: string;
  /** Character IDs who can vote */
  voters: string[];
  /** Timeout in milliseconds */
  timeoutMs: number;
  /** ISO timestamp */
  timestamp: string;
}

/**
 * Vote cast on movement proposal.
 * Sent when a character votes on a movement proposal.
 */
export interface MovementVoteCastMessage {
  type: 'movement_vote_cast';
  /** Proposal ID */
  proposalId: string;
  /** Character ID who voted */
  voterId: string;
  /** Vote choice (yes/no/abstain) */
  vote: string;
  /** Current yes vote count */
  yesCount: number;
  /** Current no vote count */
  noCount: number;
  /** Votes needed for majority */
  votesNeeded: number;
  /** ISO timestamp */
  timestamp: string;
}

/**
 * Movement approved and starting.
 * Sent when a movement proposal passes and travel begins.
 */
export interface MovementStartedMessage {
  type: 'movement_started';
  /** Target scene ID */
  targetSceneId: string;
  /** Target location ID (if travel) */
  targetLocationId?: string;
  /** Target name (human-readable) */
  targetName: string;
  /** Travel time in minutes */
  travelMinutes: number;
  /** Whether this is travel (cross-location) */
  isTravel: boolean;
  /** ISO timestamp */
  timestamp: string;
}

/**
 * Arrived at new location/scene.
 * Sent when movement completes successfully.
 */
export interface MovementCompleteMessage {
  type: 'movement_complete';
  /** New scene ID */
  sceneId: string;
  /** New scene name */
  sceneName: string;
  /** New location ID */
  locationId: string;
  /** Scene description (read-aloud text) */
  description: string;
  /** Updated in-game time (structured data) */
  inGameTime: InGameTimeData;
  /** ISO timestamp */
  timestamp: string;
}

/**
 * Movement blocked (condition not met).
 * Sent when movement cannot proceed.
 */
export interface MovementBlockedMessage {
  type: 'movement_blocked';
  /** Reason movement is blocked */
  reason: string;
  /** Hint for how to unlock (optional) */
  hint?: string;
  /** Exit ID that was blocked */
  exitId: string;
  /** ISO timestamp */
  timestamp: string;
}

/**
 * PM token balance update from the lobby-server. Pushed once on connect
 * (initial state) and on any future balance change observed server-side.
 *
 * The lobby has no central message-router envelope like the game-server's
 * `ServerMessage` union, so this is declared here as a free-standing union
 * member that the mobile + web clients dispatch on by `type`.
 */
export interface LobbyBalanceUpdateMessage {
  type: 'lobby:balance-update';
  pmTokenBalance: number;
}

// ============================================================
// Audio messages — server tells client which cue to play
// ============================================================

/**
 * "Start playing this looping background soundtrack from this URL."
 *
 * Sent by the server's per-session SoundtrackManager when a state
 * change resolves to a new background cue (scene transition,
 * encounter start, mood overlay flip, UI panel open). Client
 * AudioManager crossfades from whatever's currently on the ambient
 * channel; for looping cues, it also schedules a self-crossfade at
 * the loop boundary.
 *
 * The payload carries only what the client needs to PLAY the audio.
 * Cue ids stay server-internal (used by SoundtrackManager for change
 * detection) and never cross the wire.
 *
 * Mirrors Rust `SoundtrackPlayPayload`.
 */
export interface SoundtrackPlayMessage {
  type: 'soundtrack_play';
  /** CDN URL of the M4A to stream. */
  url: string;
  /** Whether to loop with engine-driven crossfade at the loop boundary. */
  loop: boolean;
  /** Crossfade duration (also used at loop boundary if `loop` is true). */
  fade_ms: number;
}

/**
 * "Fade out the current background soundtrack."
 *
 * Mirrors Rust `SoundtrackStopPayload`.
 */
export interface SoundtrackStopMessage {
  type: 'soundtrack_stop';
  fade_ms: number;
}

/**
 * "Play this one-shot FX; the current soundtrack is replaced for its duration,
 * then resumes on FX end (single-channel mixer)."
 *
 * Mirrors Rust `FxPlayPayload`. Used for discrete game events:
 * character_death lament, level_up motif, combat_start stab, victory
 * cadence, dragon_flyby swoop.
 */
export interface FxPlayMessage {
  type: 'fx_play';
  url: string;
  fade_in_ms?: number;
  fade_out_ms?: number;
}

/** Lobby-prefixed variants of the audio messages — same payload, lobby-stream tag. */
export interface LobbySoundtrackPlayMessage {
  type: 'lobby:soundtrack-play';
  url: string;
  loop: boolean;
  fade_ms: number;
}
export interface LobbySoundtrackStopMessage {
  type: 'lobby:soundtrack-stop';
  fade_ms: number;
}
export interface LobbyFxPlayMessage {
  type: 'lobby:fx-play';
  url: string;
  fade_in_ms?: number;
  fade_out_ms?: number;
}
